import { DEFAULT_PAGINATION } from "@/constants/table";
import { PaginationInfo } from "@/types/api";
import { Table as AntdTable, TableProps as AntdTableProps } from "antd";
import {
  ColumnGroupType,
  ColumnType,
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from "antd/es/table/interface";
import { useCallback, useEffect, useState } from "react";
import useSort, { SortCondition } from "./hooks/useSort";
import useFilter, { FilterCondition } from "./hooks/useFilter";

type TablePagination = {
  dataPagination?: PaginationInfo; // Pagination data from API
  defaultPagination?: TablePaginationConfig; // Default pagination config
  onChange?: (pagination: TablePaginationConfig) => void; // Callback function to handle pagination change
};

export type FilterOptions = {
  text: string;
  value: string | number | boolean;
};

export interface ColumnsProps<T> {
  record: T[];
  filterOptions?: Record<string, FilterOptions[]>;
  filterValues?: Record<string, FilterValue>;
  onView?: (record: T) => void;
  onEdit?: (record: T) => void;
  onDelete?: (record: T) => void;
  onRestore?: (record: T) => void;
  getSortOrder?: (field: string) => "ascend" | "descend" | undefined;
}

interface TableProps<T>
  extends Omit<AntdTableProps<T>, "pagination" | "onChange" | "columns"> {
  columns: (
    columnsProps: ColumnsProps<T>
  ) => (ColumnGroupType<T> | ColumnType<T>)[];
  pagination?: TablePagination;
  initialSort?: SortCondition;
  filterOptions?: Record<string, FilterOptions[]>;
  isHidden?: boolean;
  onSort?: (sort?: SortCondition) => void;
  onFilter?: (filters: FilterCondition[]) => void;
}

const Table = <T,>({
  columns,
  onSort,
  onFilter,
  pagination,
  initialSort,
  filterOptions,
  isHidden = false,
  ...props
}: TableProps<T>) => {
  const [paginationConfig, setPaginationConfig] =
    useState<TablePaginationConfig>(
      pagination?.defaultPagination || DEFAULT_PAGINATION
    );
  const { sort, setSort, getSortOrder } = useSort(initialSort);
  const { filters, setFilters, getFilterValues } = useFilter();

  // Handle pagination change
  const handlePaginationChange = useCallback(
    (pagination: TablePaginationConfig) => {
      setPaginationConfig((prev) => ({
        ...prev,
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
      }));
    },
    [setPaginationConfig]
  );

  // Handle sorting - only supports single column sorting for now
  // TODO: Support multiple column sorting
  const handleSortChange = (sorter: SorterResult<T> | SorterResult<T>[]) => {
    if (!Array.isArray(sorter)) {
      // When sorter.order is undefined, set sort to undefined
      if (!sorter.order) {
        setSort(undefined);
      } else {
        // Handle nested fields
        const field = Array.isArray(sorter.field)
          ? sorter.field.join(".")
          : String(sorter.field);
        const order = sorter.order === "ascend" ? "asc" : "desc";
        setSort({ field, order });
      }
    }
  };

  // Handle filter change
  const handleFilterChange = (filters: Record<string, FilterValue | null>) => {
    const newFilters: FilterCondition[] = [];
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value.length > 0) {
        const field = key; // Column key matches database field
        newFilters.push({
          field,
          operator: "in",
          values: value,
        });
      }
    });

    setFilters(newFilters);
  };

  // Trigger onSort callback
  useEffect(() => {
    onSort?.(sort);
  }, [onSort, sort]);

  // Trigger onFilter callback
  useEffect(() => {
    onFilter?.(filters || []);
  }, [onFilter, filters]);

  // Trigger onChange callback
  useEffect(() => {
    pagination?.onChange?.(paginationConfig);
  }, [pagination, paginationConfig]);

  // Update pagination total from API response
  useEffect(() => {
    const total =
      pagination?.dataPagination?.total_count || paginationConfig.total || 0;

    if (total) {
      setPaginationConfig((prev) => ({
        ...prev,
        total,
      }));
    }
  }, [pagination?.dataPagination?.total_count, paginationConfig.total]);

  // Hide table if hidden
  if (isHidden) {
    return <></>;
  }

  return (
    <AntdTable<T>
      columns={columns({
        record: props?.dataSource as T[],
        filterOptions,
        filterValues: getFilterValues(),
        getSortOrder,
      })}
      rowKey={
        props?.rowKey || ((record) => (record as any)?.id?.toString() || "")
      }
      pagination={pagination ? paginationConfig : false}
      onChange={(pagination, filters, sorter) => {
        handleSortChange(sorter);
        handleFilterChange(filters);
        handlePaginationChange(pagination);
      }}
      {...props}
    />
  );
};

export default Table;
